import React from 'react';
import * as ReactRedux from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import CustomPropTypes from 'CustomPropTypes';
import withInit from 'WithInit';
import StaticCreateAccountSection from 'StaticCreateAccountSection';
import StaticRequestSignatureContentSection from 'StaticRequestSignatureContentSection';
import ConnectedHelpSideBar from 'VisualStatic/ConnectedHelpSideBar';
import HelpSideBarButton from 'VisualStatic/HelpSideBarButton';
import StaticBuilderSection from './StaticBuilderSection';
import {
  getSessionContent,
  loadNextStaticBuilderSection,
  loadPreviousStaticBuilderSection,
  setStaticBuilderFieldOutline,
} from './StaticBuilderActions';
import StaticBuilderFormattedFields from './StaticBuilderFormattedFields';
import withStaticBuilderSetup from './hocs/StaticBuilderSetup';

export { default as CalendarField } from './CalendarField';
export { default as LineBreak } from './LineBreak';
export { default as Note } from './Note';
export { default as RadioButton } from './RadioButton';
export { default as Subheading } from './Subheading';
export { default as NavSideBarList } from './NavSideBarList';
export { default as NavSideBarMenuButton } from './NavSideBarMenuButton';
export { default as StaticBuilderForm } from './StaticBuilderForm';
export { default as StaticBuilderNavSideBarWrapper } from './StaticBuilderNavSideBarWrapper';
export { default as StaticBuilderSection } from './StaticBuilderSection';
export { default as StaticBuilderDocumentPreview } from './StaticBuilderDocumentPreview';
export { default as StaticBuilderDocumentPreviewTitle } from './StaticBuilderDocumentPreviewTitle';
export { default as StaticBuilderProgressBarWrapper } from './StaticBuilderProgressBarWrapper';
export { default as StaticBuilderProgressBar } from './StaticBuilderProgressBar';

const INCLUDE_CORP_FLOW_DOCUMENT_TYPES = [
  'ca-articles-of-incorporation-c-corp',
  'ca-articles-of-incorporation-s-corp',
  'ca-articles-of-incorporation-si',
  'irs-2553-form',
];

const INCLUDE_CUSTOM_CSS_DOCUMENT_TYPES = [
  '1099-misc-efile-2014',
  '1099-misc-efile-2015',
  '1099-misc-efile-2016',
  '1099-misc-efile-2017',
  '1099-misc-efile-2018',
  '1099-misc-efile-2019',
  '1099-misc-efile-2020',
  '1099-misc-efile-2021',
  '1099-misc-efile-2022',
  '1099-nec-efile-2020',
  '1099-nec-efile-2021',
  '1099-nec-efile-2022',
  'employee-paystub',
  'invoice-pages',
  'sample-document',
  'w2-efile-2014',
  'w2-efile-2015',
  'w2-efile-2016',
  'w2-efile-2017',
  'w2-efile-2018',
  'w2-efile-2019',
  'w2-efile-2020',
  'w2-efile-2021',
  'w2-efile-2022',
  'w9-2014-static',
];

const INCLUDE_CUSTOM_JS_DOCUMENT_TYPES = [
  '1099-misc-efile-2014',
  '1099-misc-efile-2015',
  '1099-misc-efile-2016',
  '1099-misc-efile-2017',
  '1099-misc-efile-2018',
  '1099-misc-efile-2019',
  '1099-misc-efile-2020',
  '1099-misc-efile-2021',
  '1099-misc-efile-2022',
  '1099-nec-efile-2020',
  '1099-nec-efile-2021',
  '1099-nec-efile-2022',
  'ca-articles-of-organization',
  'ca-statement-of-information',
  'employee-paystub',
  'i9-2012',
  'w2-efile-2014',
  'w2-efile-2015',
  'w2-efile-2016',
  'w2-efile-2017',
  'w2-efile-2018',
  'w2-efile-2019',
  'w2-efile-2020',
  'w2-efile-2021',
  'w2-efile-2022',
  'w9-2014-static',
];

const HELP_SIDE_BAR_DOCUMENT_MAP = {
  'w2-efile-2022': 'w2',
  '1099-misc-efile-2022': 'form1099Misc',
  '1099-nec-efile-2022': 'form1099Nec',
};

const SKIP_SHOW_REQUEST_SIGNATURE = [
  'sample-document',
];
const RENDER_HEADER_TEXT_AS_HTML = [
  'sample-document',
];

export class StaticBuilder extends React.Component {
  static loadScript(src) {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.body.appendChild(script);
  }

  constructor(props) {
    super(props);

    this.shouldInitalizeHelpSideBar = this.shouldInitalizeHelpSideBar.bind(this);
    this.initializeHelpSideBar = this.initializeHelpSideBar.bind(this);

    this.props.setStaticBuilderFieldOutline(
      this.props.documentType,
      this.props.productMap,
      this.props.initialOnStep,
      this.props.forceDownload,
    );
  }

  componentDidMount() {
    this.props.getSessionContent(
      this.props.sessionId,
      this.props.sessionHash,
      this.props.forceDownload,
    );

    if (INCLUDE_CORP_FLOW_DOCUMENT_TYPES.indexOf(this.props.documentType) >= 0) {
      StaticBuilder.loadScript(
        `${window.FORMSWIFT_URL || ''}/js/custom-document/common/corp-flow.js?v=1.4`,
      );
    }

    if (INCLUDE_CUSTOM_JS_DOCUMENT_TYPES.indexOf(this.props.documentType) >= 0) {
      StaticBuilder.loadScript(
        `${window.FORMSWIFT_URL || ''}/js/custom-document/${this.props.documentType}.js?v=1.7`,
      );
    }

    this.showRequestSignature =
      (SKIP_SHOW_REQUEST_SIGNATURE.indexOf(this.props.documentType) === -1);
    this.renderHeaderTextAsHtml = RENDER_HEADER_TEXT_AS_HTML.indexOf(this.props.documentType) >= 0;
  }

  shouldInitalizeHelpSideBar() {
    const { documentType, isVisualStatic } = this.props;
    return isVisualStatic && Object.keys(HELP_SIDE_BAR_DOCUMENT_MAP).indexOf(documentType) >= 0;
  }

  initializeHelpSideBar() {
    const builderContainer = document.getElementById('srb_content_wrapper');
    const helpButtonContainer = document.createElement('div');

    helpButtonContainer.id = 'helpButtonContainer';
    builderContainer.appendChild(helpButtonContainer);
    HelpSideBarButton.init('helpButtonContainer');
    ConnectedHelpSideBar.init('helpSideBarAnchor', {
      documentName: HELP_SIDE_BAR_DOCUMENT_MAP[this.props.documentType],
    });
  }

  render() {
    if (window.createAccountScreen === this.props.onStep) {
      return (<StaticCreateAccountSection />);
    }

    // window.showRequestSignature is set via Optimizely editor
    // Experiment ID: 8487421299 Variation: 8488201416
    if (
      ((window.User.isLoggedIn && !window.secondPresence) || window.showRequestSignature) &&
      this.props.onStep >= this.props.sectionKeys.length
    ) {
      return (
        <StaticRequestSignatureContentSection
          documentLabel={window.CurrentDocumentInfo.label.toLowerCase()}
          documentType={this.props.documentType}
        />
      );
    }

    const currentSectionKey = this.props.sectionKeys[this.props.onStep];
    const sectionKeyGroup = this.props.sectionKeyToSectionGroupKey[currentSectionKey];
    let sectionOutline = {};
    if (sectionKeyGroup) {
      sectionOutline = this.props.fieldOutline[sectionKeyGroup][currentSectionKey];
    }

    const updatedOnSectionLoadActions = [...this.props.onSectionLoadActions];

    if (this.shouldInitalizeHelpSideBar()) {
      updatedOnSectionLoadActions.push(this.initializeHelpSideBar);
    }

    return (
      <div id="loadedContentArea">
        {INCLUDE_CUSTOM_CSS_DOCUMENT_TYPES.indexOf(this.props.documentType) >= 0 &&
          <link
            rel="stylesheet"
            type="text/css"
            media="screen"
            href={`${window.FORMSWIFT_URL || ''}/styles/custom-document/${this.props.documentType}.css?v=1.1`}
          />
        }

        <StaticBuilderSection
          documentName={HELP_SIDE_BAR_DOCUMENT_MAP[this.props.documentType]}
          documentType={this.props.documentType}
          formattedFields={this.props.formattedFields}
          header={sectionOutline.header}
          inlineConditionalScripts={this.props.sectionKeyToConditionals[currentSectionKey] || ''}
          isLoading={this.props.isLoading}
          isVisualStatic={this.props.isVisualStatic}
          loadNextStaticBuilderSection={this.props.loadNextStaticBuilderSection}
          loadPreviousStaticBuilderSection={this.props.loadPreviousStaticBuilderSection}
          onSectionLoadActions={updatedOnSectionLoadActions}
          onStep={this.props.onStep}
          renderHeaderTextAsHtml={this.renderHeaderTextAsHtml}
          saveAndContinue={window.SessionManager.saveAndContinue}
          sectionKeys={this.props.sectionKeys}
          showRequestSignature={this.showRequestSignature}
        />
      </div>
    );
  }
}

const mapStateToProps = ({ StaticBuilderReducer }) => {
  let sectionKeyGroup = null;
  let currentSectionKey = null;
  let sectionOutline = {};

  if (!StaticBuilderReducer.isLoading) {
    currentSectionKey = StaticBuilderReducer.sectionKeys[StaticBuilderReducer.step];
    sectionKeyGroup = StaticBuilderReducer.sectionKeyToSectionGroupKey[currentSectionKey];
  }

  if (sectionKeyGroup) {
    sectionOutline = StaticBuilderReducer.fieldOutline[sectionKeyGroup][currentSectionKey];
  }

  return {
    fields: sectionOutline.fields,
    onStep: StaticBuilderReducer.step,
    sectionKeys: StaticBuilderReducer.sectionKeys,
    fieldOutline: StaticBuilderReducer.fieldOutline,
    sectionContent: StaticBuilderReducer.fieldData,
    sectionKeyToSectionGroupKey: StaticBuilderReducer.sectionKeyToSectionGroupKey,
    isLoading: StaticBuilderReducer.isLoading,
    onSectionLoadActions: StaticBuilderReducer.onSectionLoadActions,
    sectionKeyToConditionals: StaticBuilderReducer.sectionKeyToConditionals,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    getSessionContent,
    loadNextStaticBuilderSection,
    loadPreviousStaticBuilderSection,
    setStaticBuilderFieldOutline,
  }, dispatch)
);

export const StaticBuilderWithRedux = ReactRedux.connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStaticBuilderSetup(StaticBuilder));

StaticBuilder.defaultProps = {
  fieldOutline: null,
  forceDownload: false,
  initialOnStep: 0,
  isVisualStatic: false,
  onSectionLoadActions: [],
  onStep: 0,
  productMap: {},
  sectionKeys: [],
  sectionKeyToConditionals: {},
  sectionKeyToSectionGroupKey: {},
};

const productMapPropType = CustomPropTypes.hashmapOf(
  CustomPropTypes.hashmapOf(
    PropTypes.shape({
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          label: PropTypes.string,
          options: PropTypes.oneOfType([
            PropTypes.arrayOf(
              PropTypes.shape({
                text: PropTypes.string,
                value: PropTypes.string,
              }),
            ),
            PropTypes.string,
          ]),
          type: PropTypes.string,
        }),
      ),
      header: PropTypes.string,
    }),
  ),
);

StaticBuilder.propTypes = {
  documentType: PropTypes.string.isRequired,
  fieldOutline: productMapPropType,
  forceDownload: PropTypes.bool,
  formattedFields: PropTypes.instanceOf(StaticBuilderFormattedFields).isRequired,
  getSessionContent: PropTypes.func.isRequired,
  initialOnStep: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  isVisualStatic: PropTypes.bool,
  loadNextStaticBuilderSection: PropTypes.func.isRequired,
  loadPreviousStaticBuilderSection: PropTypes.func.isRequired,
  onSectionLoadActions: PropTypes.arrayOf(PropTypes.func),
  onStep: PropTypes.number,
  productMap: productMapPropType,
  sectionKeys: PropTypes.arrayOf(PropTypes.string),
  sectionKeyToConditionals: CustomPropTypes.hashmapOf(PropTypes.string),
  sectionKeyToSectionGroupKey: CustomPropTypes.hashmapOf(PropTypes.string),
  sessionHash: PropTypes.string.isRequired,
  sessionId: PropTypes.string.isRequired,
  setStaticBuilderFieldOutline: PropTypes.func.isRequired,
};

export default withInit(StaticBuilderWithRedux, true);



// WEBPACK FOOTER //
// ./src-react/StaticBuilder/index.jsx